import { Add, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../components/dialogs/ConfirmationDialog";
import {
  CATEGORIES_LIST,
  ModuleCategoryEnum,
  ModuleModel,
  ModuleStatusEnum,
  STATUS_COLORS,
  STATUS_LABELS,
} from "../../data/models/modules/ModuleModel";
import { toMillis } from "../../utils/StringTool";
import { useModules } from "../hooks/useModules";
import CreateEditModuleDialog from "./components/CreateEditModuleDialog";

export default function ModulesPage() {
  const { modules, createModule, loading, deleteModule } = useModules();
  const [moduleToDelete, setModuleToDelete] = useState<ModuleModel | null>(
    null
  );
  const navigate = useNavigate();

  const [openModule, setOpenModule] = useState(false);
  const [categoryTab, setCategoryTab] = useState<ModuleCategoryEnum>(
    ModuleCategoryEnum.TABLE
  );

  const handleCreateModule = () => setOpenModule(true);

  const filteredModules = modules
    ?.filter((m: ModuleModel) => m.category === categoryTab)
    ?.sort((a, b) => {
      const da = toMillis(a.createdAt);
      const db = toMillis(b.createdAt);
      return da - db;
    });

  const handleOpenModule = (mod: ModuleModel) => {
    navigate(`/dashboard/tables/orders?moduleId=${mod.id}`);
  };

  return (
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 4 },
        width: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
        overflowY: "hidden",
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Typography variant="h5" fontWeight={700}>
          Gestión de módulos
        </Typography>

        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ borderRadius: 999 }}
          onClick={handleCreateModule}
        >
          Nuevo módulo
        </Button>
      </Stack>

      {/* Tabs */}
      <Tabs
        value={categoryTab}
        onChange={(_, v) => setCategoryTab(v)}
        sx={{ mb: 3 }}
      >
        {CATEGORIES_LIST.map((cat) => (
          <Tab
            key={cat.id}
            value={cat.id}
            label={cat.name}
            sx={{ fontWeight: 600 }}
          />
        ))}
      </Tabs>

      {/* Grid de módulos */}
      <Grid container spacing={2}>
        {filteredModules?.map((mod: ModuleModel) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={mod.id}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                borderRadius: 1,
                borderLeft: `6px solid ${STATUS_COLORS[mod.status]}`,
                display: "flex",
                flexDirection: "column",
                height: "150px",
                position: "relative",
                cursor: "pointer",
                transition: "0.15s",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.03)",
                },
              }}
              onClick={() => handleOpenModule(mod)}
            >
              {mod.status === ModuleStatusEnum.CLOSING && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setModuleToDelete(mod);
                  }}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    zIndex: 2,
                    bgcolor: "rgba(0,0,0,0.05)",
                    "&:hover": { bgcolor: "rgba(255,0,0,0.15)" },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Chip
                  size="small"
                  label={STATUS_LABELS[mod.status]}
                  sx={{
                    backgroundColor: STATUS_COLORS[mod.status],
                    color: "white",
                    fontWeight: "bold",
                  }}
                />
              </Stack>

              <Typography
                fontSize={18}
                fontWeight={700}
                mt={1.5}
                sx={{ textTransform: "capitalize" }}
              >
                {mod.name}
              </Typography>

              {mod.orderId && (
                <Typography fontSize={14} color="text.secondary">
                  Orden: {mod.orderId}
                </Typography>
              )}

              {mod.userName && (
                <Typography fontSize={13} mt="auto">
                  Usuario: {mod.userName}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Dialog para crear módulo */}
      <CreateEditModuleDialog
        open={openModule}
        onClose={() => setOpenModule(false)}
        initialValue={undefined}
        onSave={async (data) => {
          await createModule(data as any);
          setOpenModule(false);
        }}
        loading={loading}
      />

      <ConfirmationDialog
        open={!!moduleToDelete}
        title="Eliminar módulo"
        message={`¿Estás seguro que deseas eliminar el módulo ${moduleToDelete?.name}`}
        onClose={() => setModuleToDelete(null)}
        confirmText="Eliminar"
        onConfirm={() => {
          deleteModule(moduleToDelete!.id!);
          setModuleToDelete(null);
        }}
      />
    </Box>
  );
}
